import { useEffect, useMemo, useRef, useState } from 'react'
import { MessageSquareWarning, Paperclip, X } from 'lucide-react'
import { redirect } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, useToast } from '../admin/primitives.js'
import { Combobox } from '../admin/pickers.js'
import { Field, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { chargerResponsables, creer, type CorpsCreation } from './signalements-api.js'
import {
  Intentions, cheminFiche, limite, modificateurPoint, refusFichier, tailleLisible, typesAcceptes, validerSaisie,
  type Droits, type Materiel, type MetaSignalements, type Responsable,
} from './signalements-model.js'
import {
  Chargement, EtatErreur, LigneMateriel, RechercheMateriel, ZoneTexte, deposerFichiers, useMetaSignalements,
} from './SignalementsCommun.js'

/**
 * SIGNALEMENTS — CRÉER (`/technique/signalements/nouveau`, 09/10/2026).
 *
 * ═══ TECHNIQUE EST LA SOURCE DE VÉRITÉ ═══
 * Technique attribue la référence (`SIG-AAAA-NNNN`), pose le statut initial,
 * la source (`gestion`) et l'auteur (le compte connecté, transmis par le
 * serveur de GESTION). Le formulaire ne propose donc NI source NI auteur.
 *
 * Une vraie page rapide, servie par la barre d'enregistrement (« Créer le
 * signalement ») : elle paraît dès qu'un champ est rempli. La demande porte
 * une clé d'idempotence par INTENTION : un double clic, un délai dépassé ou un
 * « Réessayer » repartent avec la même clé — Technique ne crée jamais deux
 * fois le même signalement. Les pièces jointes partent APRÈS la création,
 * chacune avec sa propre clé ; puis la fiche s'ouvre à la place de ce
 * formulaire (le Retour ramène à la liste, pas à un formulaire vide).
 */

export function SignalementNouveauPage() {
  const { etat, recharger } = useMetaSignalements()
  return (
    <main className="lc-page lc-page--readable lc-stagger sig-page">
      <Trail leaf="Nouveau signalement" />
      <SmartBack fallback="/technique/signalements" />
      <header className="sig-hero">
        <div>
          <h1 className="lc-title"><MessageSquareWarning size={26} aria-hidden="true" /> Nouveau signalement</h1>
          <p className="lc-subtitle">
            Décrivez ce qui ne va pas. L’équipe Technique le reçoit aussitôt, lui
            attribue une référence et en assure le suivi.
          </p>
        </div>
      </header>
      {etat.phase === 'chargement' ? <Chargement libelle="Préparation du formulaire" /> : null}
      {etat.phase === 'erreur' ? <EtatErreur erreur={etat.erreur} quoi="le formulaire" onReessayer={recharger} /> : null}
      {etat.phase === 'ok' ? <Formulaire meta={etat.meta} droits={etat.droits} /> : null}
    </main>
  )
}

interface Saisie {
  titre: string
  description: string
  categorie: string
  priorite: string
  impact: string
  emplacement: string
  responsable_id: string | null
}

const INTENTION = 'creation'

function Formulaire({ meta, droits }: { meta: MetaSignalements; droits: Droits }) {
  const toast = useToast()
  const vide = useMemo<Saisie>(() => ({
    titre: '', description: '', categorie: '', priorite: meta.priorite_defaut, impact: meta.impact_defaut,
    emplacement: '', responsable_id: null,
  }), [meta])
  const [saisie, setSaisie] = useState<Saisie>(vide)
  const [materiels, setMateriels] = useState<Materiel[]>([])
  const [fichiers, setFichiers] = useState<File[]>([])
  const [refuses, setRefuses] = useState<{ nom: string; raison: string }[]>([])
  const [erreursServeur, setErreursServeur] = useState<Record<string, string>>({})
  const [tente, setTente] = useState(false)
  const [cree, setCree] = useState(false)
  const [responsables, setResponsables] = useState<Responsable[]>([])
  // Une clé par intention, en mémoire de l'écran seulement : quitter la page, c'est abandonner l'intention.
  const intentions = useRef(new Intentions())
  useEffect(() => () => intentions.current.toutOublier(), [])

  useEffect(() => {
    if (!droits.manage) return
    let vivant = true
    void chargerResponsables().then((r) => { if (vivant && r.ok) setResponsables(r.data) })
    return () => { vivant = false }
  }, [droits.manage])

  const poser = <K extends keyof Saisie>(k: K, v: Saisie[K]) => {
    setSaisie((s) => ({ ...s, [k]: v }))
    setErreursServeur((e) => { const { [k]: _retire, ...reste } = e; return reste })
  }

  const locales = validerSaisie(saisie, meta)
  const erreur = (champ: string): string | null => erreursServeur[champ] ?? (tente ? locales[champ] ?? null : null)
  const dirty = !cree && (
    (Object.keys(vide) as (keyof Saisie)[]).some((k) => saisie[k] !== vide[k])
    || materiels.length > 0 || fichiers.length > 0
  )

  useSaveBar({
    dirty,
    saveLabel: 'Créer le signalement',
    successMessage: 'Signalement créé',
    invalid: locales['titre'] && saisie.titre.trim() === '' ? 'Donnez un titre au signalement.'
      : locales['categorie'] ? 'Choisissez une catégorie.'
        : Object.values(locales)[0] ?? null,
    onCancel: () => {
      setSaisie(vide); setMateriels([]); setFichiers([]); setRefuses([]); setErreursServeur({}); setTente(false)
      intentions.current.toutOublier()
    },
    onSave: async () => {
      setTente(true)
      if (Object.keys(locales).length > 0) return 'Certaines informations sont à corriger.'
      const corps: CorpsCreation = { titre: saisie.titre.trim(), categorie: saisie.categorie }
      if (saisie.description.trim()) corps.description = saisie.description
      if (saisie.priorite) corps.priorite = saisie.priorite
      if (saisie.impact) corps.impact = saisie.impact
      if (saisie.emplacement.trim()) corps.emplacement = saisie.emplacement.trim()
      if (droits.manage && saisie.responsable_id) corps.responsable_id = saisie.responsable_id
      if (materiels.length > 0) corps.materiel_ids = materiels.map((m) => String(m.id))
      const r = await creer(corps, intentions.current.cle(INTENTION))
      intentions.current.apres(INTENTION, r.ok ? null : r.erreur)
      if (!r.ok) {
        setErreursServeur(r.erreur.champs)
        const trace = r.erreur.traceId ? ` (réf. ${r.erreur.traceId.slice(0, 8)})` : ''
        return r.erreur.genre === 'indisponible' || r.erreur.genre === 'reseau'
          ? `${r.erreur.message} Réessayer ne créera pas de doublon.${trace}`
          : `${r.erreur.message}${trace}`
      }
      const reference = r.data.reference
      if (fichiers.length > 0) {
        const bilan = await deposerFichiers(reference, fichiers, intentions.current)
        if (bilan.echecs.length > 0) {
          toast('error', `${reference} est créé, mais ${bilan.echecs.length > 1 ? `${bilan.echecs.length} pièces jointes n’ont pas pu être déposées` : 'une pièce jointe n’a pas pu être déposée'} : ajoutez-la depuis la fiche.`, { durationMs: 9000 })
        }
      }
      setCree(true)
      // La fiche REMPLACE le formulaire dans l'historique : Retour ramène à la liste. Un remplacement
      // garde la position de défilement (router.tsx, §29) : sans remise en haut, la fiche s'ouvrait à
      // la hauteur du bas du formulaire, en-tête et actions hors de vue (recette Chrome du 09/10/2026).
      window.setTimeout(() => {
        document.querySelector('.lc-scroll')?.scrollTo({ top: 0 })
        redirect(cheminFiche(reference))
      }, 0)
      return true
    },
  })

  const ajouterFichiers = (liste: FileList | null) => {
    if (!liste) return
    const ok: File[] = []
    const ko: { nom: string; raison: string }[] = []
    for (const f of Array.from(liste)) {
      const raison = refusFichier(f, meta)
      if (raison) ko.push({ nom: f.name, raison })
      else if (!fichiers.some((x) => x.name === f.name && x.size === f.size)) ok.push(f)
    }
    const place = Math.max(0, meta.fichiers.max_par_signalement - fichiers.length)
    if (ok.length > place) {
      for (const f of ok.slice(place)) ko.push({ nom: f.name, raison: `${meta.fichiers.max_par_signalement} fichiers au plus par signalement.` })
    }
    setFichiers((x) => [...x, ...ok.slice(0, place)])
    setRefuses(ko)
  }

  const categories = meta.categories.map((c) => ({ value: c.code, label: c.label }))
  const priorites = meta.priorites.map((p) => ({ value: p.code, label: p.label, dot: modificateurPoint(p.tone) }))
  const impacts = meta.impacts.map((i) => ({ value: i.code, label: i.label, dot: modificateurPoint(i.tone) }))
  const impactChoisi = meta.impacts.find((i) => i.code === saisie.impact)

  return (
    <div className="sig-formulaire">
      <section className="lc-panel sig-section" aria-labelledby="sig-quoi">
        <div className="sig-section__corps">
          <h2 className="sig-section__titre" id="sig-quoi">Le problème</h2>
          <Field label="Titre" error={erreur('titre')} helper="Une ligne : ce qui ne va pas, et où.">
            <TextInput value={saisie.titre} onChange={(v) => poser('titre', v)} maxLength={limite(meta, 'titre')}
              placeholder="Ex. Les CDJ de ROOM 1 perdent le réseau" autoFocus ariaLabel="Titre du signalement" />
          </Field>
          <div className="sig-grille">
            <Field label="Catégorie" error={erreur('categorie')}>
              <Select value={saisie.categorie} options={categories} onChange={(v) => poser('categorie', v)}
                placeholder="Choisir une catégorie" ariaLabel="Catégorie" />
            </Field>
            <Field label="Emplacement" error={erreur('emplacement')} helper="Salle, bar, baie, accueil…">
              <TextInput value={saisie.emplacement} onChange={(v) => poser('emplacement', v)}
                maxLength={limite(meta, 'emplacement')} placeholder="Ex. ROOM 1" ariaLabel="Emplacement" />
            </Field>
            <Field label="Priorité" error={erreur('priorite')}>
              <Select value={saisie.priorite} options={priorites} onChange={(v) => poser('priorite', v)} ariaLabel="Priorité" />
            </Field>
            <Field label="Impact" error={erreur('impact')} helper={impactChoisi?.description ?? undefined}>
              <Select value={saisie.impact} options={impacts} onChange={(v) => poser('impact', v)} ariaLabel="Impact" />
            </Field>
          </div>
          <Field label="Description" error={erreur('description')} helper="Depuis quand, ce qui a été essayé, ce que voit le public.">
            <ZoneTexte value={saisie.description} onChange={(v) => poser('description', v)} rows={6}
              maxLength={limite(meta, 'description')} ariaLabel="Description" invalide={!!erreur('description')} />
          </Field>
          {droits.manage ? (
            <Field label="Responsable (facultatif)" error={erreur('responsable_id')}
              helper="Laisser vide : la personne qui prendra en charge le signalement en deviendra responsable.">
              <Combobox value={saisie.responsable_id} clearable placeholder="Personne pour l’instant" ariaLabel="Responsable"
                items={responsables.map((r) => ({ value: r.id, label: r.display_name, ...(r.can_handle ? {} : { hint: 'sans droit de traitement' }) }))}
                onChange={(v) => poser('responsable_id', v)} />
            </Field>
          ) : null}
        </div>
      </section>

      <section className="lc-panel sig-section" aria-labelledby="sig-materiel">
        <div className="sig-section__corps">
          <h2 className="sig-section__titre" id="sig-materiel">Matériel concerné <span className="sig-aide">facultatif</span></h2>
          {erreur('materiel_ids') ? <p className="lc-helper lc-helper--error" role="alert">{erreur('materiel_ids')}</p> : null}
          {materiels.length > 0 ? (
            <ul className="sig-retenus">
              {materiels.map((m) => (
                <li key={m.id} className="sig-retenu">
                  <LigneMateriel m={m} />
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer ${m.nom}`}
                    onClick={() => setMateriels((x) => x.filter((y) => y.id !== m.id))}>
                    <X size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <RechercheMateriel deja={materiels.map((m) => String(m.id))}
            onChoisir={(m) => setMateriels((x) => (x.some((y) => y.id === m.id) ? x : [...x, m]))} />
        </div>
      </section>

      <section className="lc-panel sig-section" aria-labelledby="sig-pj">
        <div className="sig-section__corps">
          <h2 className="sig-section__titre" id="sig-pj">Photos et documents <span className="sig-aide">facultatif</span></h2>
          <p className="sig-aide">
            {typesAcceptes(meta)}, moins de {tailleLisible(meta.fichiers.taille_max)} chacun. Ils sont déposés juste
            après la création du signalement.
          </p>
          {fichiers.length > 0 ? (
            <ul className="sig-retenus">
              {fichiers.map((f) => (
                <li key={`${f.name}-${f.size}`} className="sig-retenu">
                  <span className="sig-fichier__nom"><Paperclip size={14} aria-hidden="true" /> {f.name}</span>
                  <span className="sig-aide">{tailleLisible(f.size)}</span>
                  <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer ${f.name}`}
                    onClick={() => setFichiers((x) => x.filter((y) => y !== f))}>
                    <X size={16} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {refuses.length > 0 ? (
            <ul className="sig-refus" role="alert">
              {refuses.map((r) => <li key={r.nom}><strong>{r.nom}</strong> — {r.raison}</li>)}
            </ul>
          ) : null}
          <label className="lc-btn sig-depot">
            <Paperclip size={16} aria-hidden="true" /> Ajouter des fichiers
            <input type="file" multiple className="sig-depot__input"
              accept={[...meta.fichiers.types.map((t) => t.mime), ...meta.fichiers.types.map((t) => `.${t.extension}`), '.jpeg'].join(',')}
              onChange={(e) => { ajouterFichiers(e.target.files); e.target.value = '' }} />
          </label>
        </div>
      </section>
    </div>
  )
}
